// UI texts: clan. 11. 10. 2026 (owner, BACKLOG #33): the survivor groups are crews (up to five, levels, chests, the weekly
// crew contract, the horde together, the chat). Numbers come as params from shared `crews.ts`.
export default {
  title: 'Crews',
  headerMeta: 'Level {lv} · {n}/{max} · Crew Rank {r}',
  tabs: { overview: 'Crew', members: 'Members', chat: 'Chat', horde: 'Horde', ranks: 'Ranks', raid: 'Raid' },
  role: { leader: 'Leader', officer: 'Officer', member: 'Member' },
  dur: { min: '{n} min', h: '{n} h', d: '{n} days' },

  locked: {
    title: 'Crews are not open yet',
    body: 'Crews open at level {lv} or after your first week. Founding one takes level {found}.',
  },

  none: {
    kicker: 'No crew yet',
    hero: 'Five, at most',
    intro: 'Up to {n} survivors under one flag: a chat of your own, a weekly crew contract, the horde together and a perk for every crew level.',
    perk: {
      levels: 'Crew levels',
      levelsText: 'Ten levels a season, the same perks for every crew, and a chest for you at each level.',
      contract: 'Weekly crew contract',
      contractText: 'Three goals for the whole crew every week, for crew XP.',
      horde: 'The horde together',
      hordeText: 'More damage when two or more of you fight the same evening, and a shared chest.',
      chat: 'A crew of your own',
      chatText: 'A name, a tag and a flag, and a chat only your crew sees.',
    },
    suggested: 'Open crews for you',
    suggestedHint: 'Close to your level and active lately. Join in one tap.',
    browse: 'All crews',
    browseCount: '{n} crews',
    rowMeta: 'LV {lv} crew · {n}/{max} · avg LV {avg} · {active} active',
    open: 'Open',
    inviteOnly: 'Invite only',
    full: 'Full',
    join: 'Join',
    apply: 'Apply',
    applied: 'Applied',
    invited: 'You are invited',
    invitedTag: 'Invited',
    empty: 'No crews yet',
    emptyBody: 'Be the first: found one from level {lv}.',
  },

  create: {
    title: 'Found a crew',
    name: 'Name',
    namePh: 'e.g. Route Nine',
    nameHint: '3 to 24 letters, numbers or spaces',
    tag: 'Tag',
    tagPh: 'e.g. RTN',
    tagHint: '3 or 4 letters or numbers',
    description: 'Motto',
    descPh: "We were here first. We'll be here last.",
    access: 'Who can join',
    open: 'Anyone',
    inviteOnly: 'Invite or application',
    cost: 'Costs {n} Scrap · needs level {lv}',
    have: 'You have {n} Scrap · level {lv}',
    needLevel: 'Reach level {lv} to found a crew.',
    needScrap: 'You need {n} more Scrap.',
    submit: 'Found the crew · {n} Scrap',
    founded: 'Your crew is on the radio',
  },

  overview: {
    /** 28. 10. 2026 (player report): the button said only "Flag"; only the leader saves one (the others look at it). */
    editFlag: 'Edit flag',
    viewFlag: 'View flag',
    settings: 'Settings',
    membersChip: '{n}/{max} members',
    onlineChip: '{n} online',
    levelChip: 'Level {n}',
    rankChip: 'Crew Rank {n}',
    full: 'The crew is full.',
    level: 'Crew level',
    xp: '{xp} / {next} crew XP',
    xpMax: '{xp} crew XP',
    maxLevel: 'Top level: every perk is open for this season.',
    // 23. 10. 2026 (owner): the camp radio's lines left the chat for the Overview.
    radio: 'Camp radio',
    radioEmpty: 'Nothing on the radio yet. Joins, crew levels, donations and sends show up here.',
    next: 'Next: level {n}, {perk}',
    xpHow: 'Crew XP comes from your runs, the horde, donations and the weekly crew contract.',
    seasonScore: 'Crew Season Score {n}',
    seasonPlace: '#{n} of the crews',
    chests: 'Crew chests',
    chestLevel: 'Level {n} chest',
    open: 'Open',
    opened: 'Opened',
    noChests: 'A chest waits at every new crew level for each member who was in the crew {h} hours and gave crew XP since the level before.',
    contract: 'Weekly crew contract',
    contractLocked: 'Opens at crew level {n}.',
    contractEnds: 'ends in {t}',
    contractDone: 'Done',
    contractXp: '+{n} crew XP',
    contractAll: 'All three: +{n} crew XP more',
    perks: 'Perks by level',
    perkAt: 'LV {n}',
    chest: 'Chest: {text}',
    honors: 'Season honors',
    give: 'Help the crew',
    donate: 'Donate',
    donateText: 'Resources for crew XP: {left} of {max} XP left today.',
    send: 'Send supplies',
    sendText: 'To a crewmate: {left} worth left today.',
    sendLocked: 'Sending opens at crew level {n}.',
    leave: 'Leave crew',
    leaveTitle: 'Leave {name}?',
    leaveBody: 'You can join another crew after {h} hours. If you lead the crew, an officer takes over. If you are the last one, the crew is gone.',
    leaveConfirm: 'Leave the crew',
    motto: 'Motto',
    access: 'Who can join',
    save: 'Save',
  },

  chat: {
    title: 'Crew chat',
    placeholder: 'Message the crew',
    send: 'Send',
    you: 'you',
    empty: 'Quiet in here. Say something.',
    // 23. 10. 2026 (owner): a crewmate's message not read yet.
    unreadDot: 'Unread crew messages',
    unreadCount: '{n} unread',
    options: 'Message options',
    report: 'Report message',
    reportReason: 'What is wrong with it? (optional)',
    reportSend: 'Send report',
    block: 'Block {name}',
    unblock: 'Unblock {name}',
    blocked: '{n} blocked',
    rules: 'Chat rules',
    rulesText: 'Be decent: no insults, hate or threats. Bad words are hidden. Reported messages are read by the game’s admin, who can delete them, close the chat for a player or ban an account.',
    banned: 'You cannot write in the chat until {t}.',
  },

  donate: {
    title: 'Donate to the crew',
    intro: 'Resources leave your Storage for crew XP: {per} worth is 1 crew XP, at most {max} XP a day. Food, Ammo and Fuel count too.',
    have: '{n} in storage',
    all: 'All',
    less: 'Less {name}',
    more: 'More {name}',
    empty: 'Your storage has no resources to give. Bring some home from a run first.',
    submit: 'Donate · +{xp} crew XP',
    pick: 'Pick amounts',
    over: 'Too much for today: {max} worth at most.',
    done: '+{xp} crew XP',
    worth: '{v} worth',
  },

  send: {
    title: 'Send supplies',
    intro: 'Into a crewmate’s Storage, up to {cap} worth a day, sent and received. Never between two accounts on one network.',
    to: 'To',
    submit: 'Send · {v} worth',
    pick: 'Pick a crewmate and amounts',
    over: 'Over today’s limit: {left} worth left.',
    done: 'Sent to {name}',
  },

  members: {
    summary: '{n}/{max} · {online} online · sorted by role',
    invite: 'Invite',
    invitePlaceholder: 'Find a survivor by name',
    inviteSend: 'Send invite',
    inviteCopied: 'Invite link copied',
    inviteText: 'Join {name} [{tag}] in After Day: {url}',
    colSurvivor: 'Survivor',
    colLv: 'LV',
    colXp: 'XP',
    colSeen: 'Seen',
    you: '(you)',
    meta: 'LV {lv} · PWR {p}',
    seenNow: 'now',
    seenM: '{n}m',
    seenH: '{n}h',
    seenD: '{n}d',
    seenNever: '-',
    online: 'online',
    sameIp: 'Same network as a crewmate: brings no crew XP or points',
    contribution: 'Crew XP this season: {n} (runs {runs}, horde {horde}, donations {donate}, contract {contract})',
    applications: 'Applications',
    accept: 'Accept',
    decline: 'Decline',
    appMeta: 'LV {lv}',
    manageHint: 'Tap a survivor to promote, demote or remove them.',
    manage: 'Manage {name}',
    promote: 'Make officer',
    officersFull: 'A crew has {n} officers at most.',
    makeLeader: 'Hand over leadership',
    makeLeaderNote: 'You become an officer.',
    demote: 'Make member',
    kick: 'Remove from crew',
    kickConfirm: 'Remove {name} from the crew?',
    kickYes: 'Yes, remove',
    officerNote: 'Officers can remove members. Only the leader can change roles.',
    cannot: 'Your role cannot change this survivor.',
    done: 'Done',
  },

  horde: {
    tonight: 'Tonight',
    none: 'No horde scheduled yet.',
    rally: 'Call the rally',
    rallied: '{name} called the crew',
    rallyHint: 'One call a day, before the horde: every member gets a notification.',
    bonus: 'Crew bonus now: +{n}% damage for every member who fights.',
    bonusNone: 'With two of you in tonight’s horde, live or on guard, the crew gets +{n}% damage.',
    bonusLocked: 'The horde bonus opens at crew level {n}.',
    who: 'Who is in',
    guard: 'on guard: {n} waves paid',
    live: 'fighting now',
    waves: '{n} waves · {d} damage',
    nobody: 'Nobody yet. Stand guard or join the horde.',
    open: 'Open the horde',
    damage: 'Crew damage: {n}',
    boards: 'Crews in the horde',
  },

  ranks: {
    title: 'Crew leaderboards',
    evening: 'Tonight',
    week: 'This week',
    boss: 'Sunday boss',
    score: 'Season Score',
    level: 'Crew level',
    empty: 'No crews here yet.',
    lv: 'LV {n}',
    damage: '{n} damage',
    points: '{n} points',
  },

  raid: {
    badge: 'Crew raid',
    title: 'Raid on {zone}',
    endsIn: 'Ends in',
    defending: 'defending',
    attacking: 'attacking',
    unclaimed: 'Unclaimed',
    noHolder: 'no holder',
    vs: 'VS',
    explainDefend: 'Keep the lead when the timer runs out and {zone} stays ours, protected for another 72 hours.',
    explainAttack: 'Lead when the timer runs out and {zone} is ours: +10% loot there for every member.',
    explainUnclaimed: 'Nobody holds {zone}. Score before the timer runs out and it is ours.',
    points: 'Recent points',
    live: 'Live',
    noPoints: 'No points yet. Every fight won in the zone scores.',
    send: 'Send survivor to raid',
    sent: '{name} is in the raid',
    sendSub: '{n} min run · every fight won scores for {clan}',
    sentSub: 'Back in {n} min · every fight won scores for {clan}',
    watch: 'Watch the run',
    away: 'Survivor is away',
    awaySub: 'Your survivor has to be back at camp first.',
    noEnergy: 'Not enough energy',
    noEnergySub: 'The run costs {n} energy.',
    otherRaid: 'Survivors you send go to the newest raid: {zone}.',
    over: 'Raid on {zone} is over',
    stampWon: 'Zone taken',
    stampHeld: 'Zone held',
    stampLost: 'Zone lost',
    stampFailed: 'Raid failed',
    finalScore: 'Final score {a} : {b}',
    ended: 'ended {t}',
    none: 'No raid right now',
    noneBody: 'Raids last 24 hours. Both crews send survivors into the zone and every fight won scores. The crew with more points holds the zone, which is then protected for 72 hours.',
    start: 'Start a raid',
    heldBy: 'held by {name}',
    busyZone: 'raid already running',
    protectedZone: 'protected until {time}',
    yours: 'held by your crew',
    free: 'unclaimed',
    startBtn: 'Start raid on {zone}',
    pickZone: 'Pick a zone',
    maxZones: 'A crew can hold at most {n} zones.',
    leaderOnly: 'Only the leader and officers can start a raid.',
    started: 'Raid started. Send your survivors.',
  },

  emblem: {
    title: 'Crew flag',
    shape: 'Shape',
    symbol: 'Symbol',
    field: 'Field color',
    ink: 'Symbol color',
    sizes: '48 · 24',
    lowContrast: 'These two colors are too close. The symbol will vanish at small sizes.',
    saved: 'Saved',
    more: 'Pick more contrast',
    save: 'Save flag · leader only',
    leaderOnly: 'Only the leader can change the flag',
    locked: 'Crew level {n}',
    honor: 'A season honor',
  },
} as const;
